import axios, { AxiosInstance, CanceledError } from "axios";
import env from "@beam-australia/react-env";
import { signOut } from "next-auth/react";
import { isValidAccessToken } from "utils/accessToken";

const isBrowser = typeof window !== "undefined";

const baseURL = isBrowser ? env("KFI_API_BASE") : process.env.KFI_AUTH_BASE;

// We know accessToken is returned as a property of client in the client.interceptors.request block
// however typescript says it's not a property of AxiosInstance so we are extending the type
// to incorporate accessToken
interface CustomAxiosInstance extends AxiosInstance {
  accessToken?: string | null;
}

const client = axios.create({
  baseURL,
  validateStatus: (status) => status !== 401,
}) as CustomAxiosInstance;

// These endpoints are used before a user has an authenticated session.
const publicEndpoints = {
  post: ["/oauth/token", "/auth/authenticate", "/passwords", "/unlock_account"],
  patch: ["/accept_invitation", "/passwords", "/unlock_account"],
};

const isPublicRequest = (method = "get", url = "") => {
  const path = url.split("?")[0];
  if (method === "get") {
    return (
      /^\/preferences\/[^/]+$/.test(path) ||
      /^\/dashboard\/blog_promo\/preview\/[^/]+$/.test(path)
    );
  }
  return publicEndpoints[method]?.includes(path) ?? false;
};

client.interceptors.response.use(
  ({ data }) => data,
  (error) => {
    // Missing-token cancellation must not start a competing logout redirect.
    if (axios.isCancel(error)) return Promise.reject(error);
    return signOut();
  }
);

client.interceptors.request.use((config) => {
  if (isPublicRequest(config.method, config.url)) {
    config.headers.delete("Authorization");
  } else if (isValidAccessToken(client.accessToken)) {
    config.headers["Authorization"] = `Bearer ${client.accessToken}`;
  } else {
    throw new CanceledError("A valid access token is required", config);
  }

  return config;
});

export default client;
